import LocalWallpaper from "@/classes/local-wallpaper";
import styles from "@/components/wallpaper.module.css";
import presetWallpapers from "@/data/preset-wallpapers";
import { globals } from "@/globals";
import useBingWallpaper from "@/hooks/use-bing-wallpaper";
import * as signals from "@/signals";
import { blurInput, toggleSecondaryScreen } from "@/utils";
import { useSignalEffect } from "@preact/signals";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, RefObject, useRef, useState } from "react";

interface WallpaperProps {
	searchInputRef: RefObject<HTMLInputElement | null>;
}

function Wallpaper({ searchInputRef }: WallpaperProps): JSX.Element {
	const [isOverlayShown, setIsOverlayShown] = useState<boolean>(false);
	const [isImageShown, setIsImageShown] = useState<boolean>(false);
	const [isFallbackUsed, setIsFallbackUsed] = useState<boolean>(false);

	const imageRef = useRef<HTMLImageElement>(null);

	const { bingWallpaperUrl, bingWallpaperUrlUhd } = useBingWallpaper();

	const handleImageError = (): void => {
		if (!isFallbackUsed && signals.wallpaper.value.fallbackUrl) {
			setIsFallbackUsed(true);
		}
	};

	const handleImageLoad = (): void => {
		window.setTimeout(() => {
			setIsImageShown(true);
		}, 50);
		window.setTimeout(() => {
			setIsOverlayShown(true);
		}, 100);
	};

	const handleWallpaperClick = (): void => {
		if (
			signals.isSecondaryScreenShown.value ||
			(globals.isMobile && !signals.isSearchFocused.value)
		) {
			void toggleSecondaryScreen();
			return;
		}
		void blurInput();
		signals.isWallpaperFocused.value = false;
	};

	const handleWallpaperContextMenu = (): void => {
		if (signals.isSecondaryScreenShown.value) {
			return;
		}
		if (globals.isAndroid) {
			searchInputRef.current?.focus();
		} else {
			void toggleSecondaryScreen();
		}
	};

	const loadWallpaper = (): void => {
		const { currentWallpaper } = signals.generalSettings.value;
		if (currentWallpaper === "Bing") {
			signals.wallpaper.value = {
				isVideo: false,
				url:
					signals.generalSettings.value.bingWallpaperResolution ===
					"uhd"
						? bingWallpaperUrlUhd
						: bingWallpaperUrl,
			};
		} else if (currentWallpaper === "Custom") {
			const localWallpaper = LocalWallpaper.getInstance();
			void localWallpaper.loadWallpaper();
		} else if (currentWallpaper?.startsWith("Default")) {
			setPresetStaticWallpaper(currentWallpaper);
		} else if (currentWallpaper?.startsWith("Live")) {
			if (!signals.liveWallpaperPrefix.value) {
				return;
			}
			if (!globals.isExtension) {
				setPresetStaticWallpaper("Default3");
				return;
			}
			const wallpaper = presetWallpapers.live.find(
				(wallpaper) => wallpaper.id === currentWallpaper,
			);
			if (wallpaper) {
				signals.wallpaper.value = {
					isVideo: true,
					url:
						signals.liveWallpaperPrefix.value +
						currentWallpaper +
						".mp4",
				};
			}
		} else {
			signals.wallpaper.value = {
				isVideo: false,
				url: currentWallpaper,
			};
		}
	};

	const setPresetStaticWallpaper = (id: string): void => {
		const wallpaper = presetWallpapers.static.find(
			(wallpaper) => wallpaper.id === id,
		);
		if (wallpaper) {
			signals.wallpaper.value = {
				fallbackUrl: wallpaper.fallbackUrl,
				isVideo: false,
				url: wallpaper.url,
			};
		}
	};

	useSignalEffect(() => {
		if (signals.wallpaper.value.url) {
			return;
		}
		loadWallpaper();
	});

	useSignalEffect(() => {
		if (signals.wallpaper.value.isVideo) {
			handleImageLoad();
		}
	});

	return (
		<>
			{signals.wallpaper.value.isVideo ? (
				<video
					autoPlay={true}
					className={clsx(
						styles["background-image"],
						signals.isWallpaperFocused.value &&
							!signals.generalSettings.value.isLiteModeEnabled &&
							styles["focus-blur"],
						signals.isWallpaperFocused.value &&
							!signals.generalSettings.value.shouldReduceMotion &&
							styles["focus-scale"],
						isImageShown && styles["show"],
					)}
					loop={true}
					muted={true}
					src={signals.wallpaper.value.url}
				></video>
			) : (
				<img
					alt=""
					className={clsx(
						styles["background-image"],
						signals.isWallpaperFocused.value &&
							!signals.generalSettings.value.isLiteModeEnabled &&
							styles["focus-blur"],
						signals.isWallpaperFocused.value &&
							!signals.generalSettings.value.shouldReduceMotion &&
							styles["focus-scale"],
						isImageShown && styles["show"],
					)}
					onError={handleImageError}
					onLoad={handleImageLoad}
					ref={imageRef}
					src={
						isFallbackUsed
							? signals.wallpaper.value.fallbackUrl
							: signals.wallpaper.value.url
					}
				/>
			)}
			<div
				aria-label={t("blankSpace")}
				className={clsx(
					styles["background-overlay"],
					signals.isWallpaperFocused.value &&
						signals.generalSettings.value.isLiteModeEnabled &&
						styles["focus-lite"],
					isOverlayShown && styles["show"],
					signals.generalSettings.value
						.shouldAddVignettingToWallpaper && styles["vignetting"],
				)}
				onClick={handleWallpaperClick}
				onContextMenu={handleWallpaperContextMenu}
				onKeyDown={() => {}} // conflicts with focusing the search bar
				role="button"
				tabIndex={0}
			></div>
		</>
	);
}

export default Wallpaper;
